import { useEffect, useState } from "react";
import { NoteFile } from "@/data/notes";
import { NoteImage } from "@/components/NoteImage";
import { notifyHost } from "@/hooks/useEmbedMessaging";

/**
 * Renders the handwritten lesson notes inline. Single unified strip — all the
 * pages for this lesson, in order. Click any page to open it full-screen
 * (still inline, still on our domain).
 */

export interface NotesGalleryProps {
  notes: NoteFile[];
  accent: string;
}

export function NotesGallery({ notes, accent }: NotesGalleryProps) {
  const [zoomed, setZoomed] = useState<NoteFile | null>(null);

  const openZoomed = (n: NoteFile) => { setZoomed(n); notifyHost("companion-modal-open"); };
  const closeZoomed = () => { setZoomed(null); notifyHost("companion-modal-close"); };

  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeZoomed(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);

  if (!notes.length) return null;

  return (
    <section id="lesson-notes" className="mx-auto w-full max-w-5xl px-4 py-5 lg:px-8 lg:py-6">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <div
            className="text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{ color: accent }}
          >
            ⊟ Handwritten notes
          </div>
          <span className="text-[11px] text-nsm-dim">
            ({notes.length} {notes.length === 1 ? "page" : "pages"} — click to expand)
          </span>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1">
        {notes.map((n, i) => (
          <button
            key={`${n.src}-${i}`}
            onClick={() => openZoomed(n)}
            className="group relative flex-shrink-0 overflow-hidden rounded-lg border border-nsm-border bg-white p-1 text-left shadow-md transition hover:border-amber-500/60"
            style={{ width: 160, height: 120 }}
            title={n.name ?? `Page ${i + 1}`}
          >
            <NoteImage src={n.src} alt={n.name ?? `Notes page ${i + 1}`} />
            <span className="absolute bottom-1 left-1 rounded bg-zinc-900/80 px-1.5 py-0.5 text-[10px] font-mono text-white">
              {i + 1}
            </span>
          </button>
        ))}
      </div>

      {zoomed && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={closeZoomed}
        >
          <div
            className="relative max-h-[95vh] w-full max-w-6xl overflow-auto rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeZoomed}
              className="absolute right-3 top-3 z-10 rounded-full bg-zinc-900/90 px-3 py-1 text-xs font-semibold text-white shadow"
            >
              Close (Esc)
            </button>
            <div className="p-2">
              <NoteImage src={zoomed.src} alt={zoomed.name ?? "Lesson notes"} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
